import {
  getWorkflowRun,
  listWorkflowRuns,
  listPortfolioFiles,
  runWorkflow,
  type CoworkPortfolioWorkflow,
  type CoworkWorkflowRun,
  type CoworkWorkflow,
} from "@/cowork/api"
import { useLanguage } from "@/context/language"
import { BackLabel } from "@/components/back-label"
import { useServer } from "@/context/server"
import { useTabs } from "@/context/tabs"
import { useCoworkWorkspace } from "@/cowork/workspace-context"
import { coworkAccountingReportTemplates } from "@/cowork/report-templates"
import { openCoworkChat } from "@/cowork/open-chat"
import { CoworkWorkflowWizard } from "./cowork-workflow-wizard"
import { CoworkWorkflowPipeline, workflowPipelineTab, WorkflowPipelineTabs } from "./cowork-workflow-pipeline"
import type { WorkflowTabState } from "@/cowork/workflow-activity"
import { useSearchParams } from "@solidjs/router"
import { useAppNavigate } from "@/cowork/workspace-route"
import { createEffect, createMemo, createResource, For, on, onCleanup, Show } from "solid-js"
import { createStore } from "solid-js/store"
import {
  createWorkflowRunAttempt,
  groupWorkflowRunValidationFindings,
  readReportingConfig,
  workflowExecutionDetails,
  workflowRunFileExtractionStatuses,
  workflowRunIsActive,
  workflowRunStage,
  workflowRunValidationFindings,
  workflowRunIndependentReview,
  workflowRunReviewHistory,
  workflowSnapshotFiles,
  workflowSourceFileAvailable,
} from "./cowork-workflow-reporting"

const chatReasonKeys = {
  missing_session: "cowork.enterprise.workflows.reporting.chat.missingSession",
  session_deleted: "cowork.enterprise.workflows.reporting.chat.deleted",
  not_owner: "cowork.enterprise.workflows.reporting.chat.notOwner",
  scope_mismatch: "cowork.enterprise.workflows.reporting.chat.scopeMismatch",
} as const
const errorCategoryKeys: Record<string, keyof typeof import("@/i18n/en").dict> = {
  input_unavailable: "cowork.enterprise.workflows.reporting.reason.inputUnavailable",
  runtime_unavailable: "cowork.enterprise.workflows.reporting.reason.runtimeUnavailable",
}
const progressSteps = ["starting", "processing_files", "analyzing", "generating_report", "ready"] as const
const validationStatusKeys = {
  passed: "cowork.enterprise.workflows.reporting.validation.passed",
  passed_with_warnings: "cowork.enterprise.workflows.reporting.validation.passedWithWarnings",
  incomplete: "cowork.enterprise.workflows.reporting.validation.incomplete",
  failed: "cowork.enterprise.workflows.reporting.validation.failed",
} as const
const findingKeys = {
  blocking: "cowork.enterprise.workflows.reporting.findings.blocking",
  incomplete: "cowork.enterprise.workflows.reporting.findings.incomplete",
  warning: "cowork.enterprise.workflows.reporting.findings.warning",
} as const
const extractionStatusKeys = {
  completed: "cowork.enterprise.workflows.reporting.extraction.completed",
  partial: "cowork.enterprise.workflows.reporting.extraction.partial",
  unreadable: "cowork.enterprise.workflows.reporting.extraction.unreadable",
  unsupported: "cowork.enterprise.workflows.reporting.extraction.unsupported",
  failed: "cowork.enterprise.workflows.reporting.extraction.failed",
} as const

export function CoworkReportingWorkflowDetail(props: { workflow: CoworkPortfolioWorkflow }) {
  const [pipelineStatus, setPipelineStatus] = createStore<WorkflowTabState>({ complete: {}, active: [] })
  const language = useLanguage()
  const navigate = useAppNavigate()
  const [query, setQuery] = useSearchParams()
  const context = useCoworkWorkspace()
  const server = useServer()
  const tabs = useTabs()
  const [state, setState] = createStore({
    runs: [] as CoworkWorkflowRun[],
    updated: undefined as CoworkWorkflow | undefined,
    editing: false,
    loading: true,
    refreshing: false,
    listError: false,
    pollError: false,
    actionError: "" as "" | "startFailed" | "trialReportLimit" | "downloadFailed" | "chatFailed",
    starting: false,
    startPending: false,
    opening: false,
  })
  const workflow = () => state.updated ?? props.workflow
  const config = createMemo(() => readReportingConfig(workflow().workflow_metadata.reporting))
  const [sources, sourceActions] = createResource(
    () => `${props.workflow.company_id}:${props.workflow.project_id}`,
    () => listPortfolioFiles({ entityID: props.workflow.company_id, projectID: props.workflow.project_id }),
  )
  const configured = () =>
    !!config() &&
    !sources.loading &&
    !sources.error &&
    config()!.file_ids.every((id) =>
      sources()?.some(
        (file) =>
          file.id === id &&
          workflowSourceFileAvailable(file, {
            companyID: props.workflow.company_id,
            projectID: props.workflow.project_id,
            periodID: config()?.period_id,
          }),
      ),
    )
  const scopeKey = createMemo(() => `${props.workflow.company_id}:${props.workflow.project_id}:${props.workflow.id}`)
  const selectedID = createMemo(() => {
    const id = Array.isArray(query.run) ? query.run[0] : query.run
    return id || state.runs[0]?.id || ""
  })
  const selected = createMemo(() => state.runs.find((run) => run.id === selectedID()))
  const tab = () => query.tab === "runs" ? "runs" : workflowPipelineTab(query.tab)
  const files = createMemo(() => workflowSnapshotFiles(selected()?.input_snapshot ?? {}))
  const execution = createMemo(() => workflowExecutionDetails(selected()?.input_snapshot ?? {}))
  const findings = createMemo(() => selected() ? workflowRunValidationFindings(selected()!) : [])
  const independentReview = createMemo(() => selected() ? workflowRunIndependentReview(selected()!) : undefined)
  const reviewHistory = createMemo(() => selected() ? workflowRunReviewHistory(selected()!) : [])
  const findingGroups = createMemo(() => groupWorkflowRunValidationFindings(findings()))
  const extractionStatuses = createMemo(() => selected() ? workflowRunFileExtractionStatuses(selected()!) : [])
  const template = createMemo(() => coworkAccountingReportTemplates.find((item) => item.id === config()?.template_id))
  const button = "rounded-lg border border-v2-border-border-base px-4 py-2 text-[12px] disabled:opacity-50"
  let disposed = false
  let refreshSelected: (() => Promise<void>) | undefined
  let reloadList: (() => Promise<void>) | undefined
  const inFlight = new Map<string, Promise<CoworkWorkflowRun>>()
  onCleanup(() => {
    disposed = true
  })

  createEffect(
    on(scopeKey, () => {
      let cancelled = false
      let loading = false
      const item = props.workflow
      setState({ runs: [], loading: true, listError: false, pollError: false, actionError: "" })
      const load = async () => {
        if (loading || cancelled) return
        loading = true
        setState({ loading: true, listError: false })
        try {
          const runs = await listWorkflowRuns(item.project_id, item.id, item.company_id)
          if (!cancelled)
            setState("runs", (current) =>
              [
                ...current,
                ...runs.filter(
                  (run) =>
                    run.workflow_id === item.id &&
                    run.project_id === item.project_id &&
                    run.company_id === item.company_id &&
                    !current.some((entry) => entry.id === run.id),
                ),
              ].sort((a, b) => b.created_at.localeCompare(a.created_at)),
            )
        } catch {
          if (!cancelled) setState("listError", true)
        } finally {
          loading = false
          if (!cancelled) setState("loading", false)
        }
      }
      reloadList = load
      void load()
      onCleanup(() => {
        cancelled = true
        reloadList = undefined
      })
    }),
  )

  createEffect(
    on(
      () => [scopeKey(), selectedID()] as const,
      ([, runID]) => {
        let cancelled = false
        let busy = false
        let timer: ReturnType<typeof setTimeout> | undefined
        const item = props.workflow
        setState({ pollError: false, refreshing: false })
        const refresh = async () => {
          if (!runID || busy || cancelled) return
          clearTimeout(timer)
          busy = true
          setState("refreshing", true)
          try {
            let request = inFlight.get(runID)
            if (!request) {
              request = getWorkflowRun(item.project_id, item.id, runID, item.company_id)
              inFlight.set(runID, request)
              void request.finally(() => inFlight.delete(runID)).catch(() => {})
            }
            const run = await request
            if (cancelled) return
            if (
              run.id !== runID ||
              run.workflow_id !== item.id ||
              run.project_id !== item.project_id ||
              run.company_id !== item.company_id
            )
              throw new Error()
            setState("runs", (runs) =>
              (runs.some((entry) => entry.id === run.id)
                ? runs.map((entry) => (entry.id === run.id ? run : entry))
                : [run, ...runs]
              ).sort((a, b) => b.created_at.localeCompare(a.created_at)),
            )
            setState("pollError", false)
            if (workflowRunIsActive(run)) timer = setTimeout(() => void refresh(), 3000)
          } catch {
            if (!cancelled) setState("pollError", true)
          } finally {
            busy = false
            if (!cancelled) setState("refreshing", false)
          }
        }
        refreshSelected = refresh
        void refresh()
        onCleanup(() => {
          cancelled = true
          clearTimeout(timer)
          refreshSelected = undefined
        })
      },
    ),
  )

  type RunIntent = { workflow: CoworkPortfolioWorkflow; periodID?: string; rerunOfRunID?: string }
  const attempt = createWorkflowRunAttempt((intent: RunIntent, requestID) =>
    runWorkflow(intent.workflow.project_id, intent.workflow.id, intent.periodID, {
      requestID,
      rerunOfRunID: intent.rerunOfRunID,
      companyID: intent.workflow.company_id,
      applicationLocale: language.locale(),
    }),
  )
  const generate = async (rerun?: CoworkWorkflowRun) => {
    const item = props.workflow
    const key = scopeKey()
    if (state.starting || item.status !== "active") return
    if (!state.startPending && (!configured() || (rerun && !readReportingConfig(rerun.input_snapshot.reporting)))) {
      setState("editing", true)
      return
    }
    setState({ starting: true, startPending: true, actionError: "" })
    try {
      const run = await attempt.start({
        workflow: item,
        periodID: rerun ? undefined : (config()?.period_id ?? undefined),
        rerunOfRunID: rerun?.id,
      })
      if (disposed || key !== scopeKey() || !run) return
      setState("runs", (runs) => [run, ...runs.filter((entry) => entry.id !== run.id)])
      setState("startPending", false)
      setQuery({ tab: "runs", run: run.id })
    } catch (error) {
      const trial = error instanceof Error && error.message === "trial_report_limit"
      if (!disposed && key === scopeKey()) setState("actionError", trial ? "trialReportLimit" : "startFailed")
    } finally {
      if (!disposed && key === scopeKey()) setState("starting", false)
    }
  }

  const openChat = async (run: CoworkWorkflowRun) => {
    if (!run.can_open_chat || !run.session_id || state.opening || selectedID() !== run.id) return
    const key = scopeKey()
    const scope = [context.state.entityId, context.state.projectId, context.state.periodId].join(":")
    const isCurrent = () =>
      !disposed &&
      scopeKey() === key &&
      selectedID() === run.id &&
      selected()?.can_open_chat === true &&
      selected()?.session_id === run.session_id &&
      [context.state.entityId, context.state.projectId, context.state.periodId].join(":") === scope
    setState({ opening: true, actionError: "" })
    try {
      await openCoworkChat(
        {
          companyID: run.company_id,
          projectID: run.project_id,
          periodID: run.period_id,
          sessionID: run.session_id,
          remoteSessionID: run.session_remote_id,
          title: workflow().name,
        },
        { context, server, tabs, isCurrent },
      )
    } catch {
      if (isCurrent()) setState("actionError", "chatFailed")
    } finally {
      if (!disposed) setState("opening", false)
    }
  }

  return (
    <div class="flex h-full min-h-0 flex-col px-4 py-4 sm:px-6" classList={{ "overflow-y-auto": tab() === "runs" || state.editing, "overflow-hidden": tab() !== "runs" && !state.editing }}>
      <button type="button" class="mb-3 shrink-0 self-start text-[12px] text-v2-text-text-muted" onClick={() => navigate("/workflows")}>
        <BackLabel>{language.t("cowork.enterprise.workflows.back")}</BackLabel>
      </button>
      <header class="flex shrink-0 flex-wrap items-start justify-between gap-4">
        <div class="min-w-0">
          <h1 class="break-words text-[30px] font-semibold">{workflow().name}</h1>
          <p class="mt-2 text-[12px] text-v2-text-text-muted">
            {props.workflow.company_name} / {props.workflow.project_name}
          </p>
          <Show when={template()}>{(item) => <p class="mt-2 text-[13px]">{language.t(item().titleKey)}</p>}</Show>
        </div>
        <div class="flex flex-wrap gap-2">
          <Show when={state.runs.length}>
            <select class={`${button} max-w-[260px]`} aria-label={language.t("cowork.enterprise.workflows.runs")}
              value={selectedID()} onChange={(event) => setQuery({ run: event.currentTarget.value }, { replace: false })}>
              <For each={state.runs}>{(run) => <option value={run.id}>{language.formatDateTime(new Date(run.created_at).getTime())}</option>}</For>
            </select>
          </Show>
          <button type="button" class={button} onClick={() => setQuery({ tab: "runs" }, { replace: false })}>
            {language.t("cowork.enterprise.workflows.reporting.viewRuns")}
          </button>
          <button
            type="button"
            class={button}
            disabled={state.editing || state.starting || state.startPending}
            onClick={() => setState("editing", true)}
          >
            {language.t(
              config()
                ? "cowork.enterprise.workflows.reporting.edit"
                : "cowork.enterprise.workflows.reporting.configure",
            )}
          </button>
          <button
            type="button"
            class="rounded-lg bg-v2-background-bg-contrast px-5 py-3 text-[12px] font-semibold text-v2-text-text-contrast disabled:opacity-50"
            disabled={
              state.editing ||
              state.starting ||
              state.startPending ||
              !configured() ||
              props.workflow.status !== "active"
            }
            onClick={() => void generate()}
          >
            {language.t("cowork.enterprise.workflows.reporting.generateAgain")}
          </button>
        </div>
      </header>
      <Show when={state.editing}>
        <CoworkWorkflowWizard
          workflow={workflow()}
          scope={{
            companyID: props.workflow.company_id,
            projectID: props.workflow.project_id,
            periodID: config()
              ? config()?.period_id
              : context.state.entityId === props.workflow.company_id &&
                  context.state.projectId === props.workflow.project_id &&
                  context.state.periodId !== context.allPeriodsID
                ? context.state.periodId || undefined
                : undefined,
            label: `${props.workflow.company_name} / ${props.workflow.project_name}`,
          }}
          onCancel={() => {
            setState("editing", false)
            void sourceActions.refetch()
          }}
          onSaved={(saved) => {
            setState({ updated: saved, editing: false })
            void sourceActions.refetch()
          }}
          onStarted={(workflowID, runID, saved) => {
            setState({ updated: saved, editing: false })
            navigate(`/workflows/${workflowID}?tab=runs&run=${encodeURIComponent(runID)}`)
            void sourceActions.refetch()
            void reloadList?.()
          }}
        />
      </Show>
      <p class="mt-3 text-[12px] text-v2-text-text-muted">
        {language.t("cowork.enterprise.workflows.reporting.freshChat")}
      </p>
      <Show when={!config() || (!sources.loading && !sources.error && !configured())}>
        <p role="alert" class="mt-4 text-[12px] text-v2-state-fg-danger">
          {language.t("cowork.enterprise.workflows.reporting.configureRequired")}
        </p>
      </Show>
      <Show when={sources.error}>
        <p role="alert" class="mt-3 text-[12px]">
          {language.t("cowork.enterprise.workflows.reporting.filesLoadFailed")}
        </p>
        <button type="button" class={button} onClick={() => void sourceActions.refetch()}>
          {language.t("cowork.enterprise.workflows.reporting.retry")}
        </button>
      </Show>
      <Show when={state.actionError}>
        <div role="alert" class="mt-4 rounded-lg bg-v2-state-bg-danger p-4 text-[12px] text-v2-state-fg-danger">
          {language.t(`cowork.enterprise.workflows.reporting.${state.actionError || "startFailed"}`)}
        </div>
      </Show>
      <Show when={state.startPending}>
        <button type="button" class={`${button} mt-3`} disabled={state.starting} onClick={() => void generate()}>
          {language.t(
            state.starting
              ? "cowork.enterprise.workflows.reporting.saving"
              : "cowork.enterprise.workflows.reporting.retryStart",
          )}
        </button>
      </Show>
      <WorkflowPipelineTabs tab={tab()} status={pipelineStatus} onSelect={(value) => setQuery({ tab: value }, { replace: false })} />
      <Show when={tab() !== "runs" && !state.editing}>
        <div class="mt-3 min-h-0 flex-1">
          <CoworkWorkflowPipeline workflow={props.workflow} run={selected()} tab={workflowPipelineTab(query.tab)} onStatus={setPipelineStatus}
            configuredFiles={sources.error ? [] : (sources() ?? []).filter((file) => config()?.file_ids.includes(file.id))}
            periodID={config()?.period_id}
            loading={state.loading || (!!selectedID() && !selected() && state.refreshing) || (!selectedID() && sources.loading)}
            error={state.listError || state.pollError || (!!selectedID() && !selected() && !state.loading && !state.refreshing)}
            onFilesChanged={() => sourceActions.refetch()} />
        </div>
      </Show>
      <Show when={tab() === "runs"}>
      <div class="mt-6 grid shrink-0 gap-5 lg:grid-cols-[260px_minmax(0,1fr)]">
        <aside class="rounded-xl border border-v2-border-border-base bg-v2-background-bg-base p-4">
          <h2 class="text-[14px] font-semibold">{language.t("cowork.enterprise.workflows.reporting.history")}</h2>
          <Show when={state.loading}>
            <p role="status" class="mt-3 text-[12px]">
              {language.t("common.loading")}
            </p>
          </Show>
          <Show when={state.listError}>
            <div role="alert" class="mt-3 text-[12px]">
              <p>{language.t("cowork.enterprise.workflows.reporting.historyFailed")}</p>
              <button
                type="button"
                class={`${button} mt-2`}
                disabled={state.loading}
                onClick={() => void reloadList?.()}
              >
                {language.t("cowork.enterprise.workflows.reporting.retry")}
              </button>
            </div>
          </Show>
          <Show when={!state.loading && !state.listError && !state.runs.length}>
            <p class="mt-3 text-[12px] text-v2-text-text-muted">{language.t("cowork.enterprise.workflows.noRuns")}</p>
          </Show>
          <ul class="mt-3 max-h-[520px] space-y-2 overflow-y-auto">
            <For each={state.runs}>
              {(run) => (
                <li>
                  <button
                    type="button"
                    class="w-full rounded-lg border border-v2-border-border-base p-3 text-left text-[12px]"
                    classList={{
                      "border-v2-border-border-strong bg-v2-background-bg-layer-01": selectedID() === run.id,
                    }}
                    aria-pressed={selectedID() === run.id}
                    onClick={() => setQuery({ run: run.id })}
                  >
                    <span class="block">{language.formatDateTime(new Date(run.created_at))}</span>
                    <span class="mt-1 block text-v2-text-text-muted">
                      {language.t(`cowork.enterprise.workflows.reporting.stage.${workflowRunStage(run)}`)}
                    </span>
                  </button>
                </li>
              )}
            </For>
          </ul>
        </aside>
        <main class="min-w-0 rounded-xl border border-v2-border-border-base bg-v2-background-bg-base p-5">
          <Show when={state.pollError}>
            <div role="alert" class="mb-4 rounded-lg bg-v2-state-bg-danger p-3 text-[12px] text-v2-state-fg-danger">
              <p>{language.t("cowork.enterprise.workflows.reporting.refreshFailed")}</p>
              <button
                type="button"
                class={`${button} mt-2`}
                disabled={state.refreshing}
                onClick={() => void refreshSelected?.()}
              >
                {language.t("cowork.enterprise.workflows.reporting.retry")}
              </button>
            </div>
          </Show>
          <Show
            when={selected()}
            fallback={
              <p class="text-[13px] text-v2-text-text-muted">
                {language.t(
                  state.refreshing
                    ? "common.loading"
                    : selectedID()
                      ? "cowork.enterprise.workflows.reporting.runUnavailable"
                      : "cowork.enterprise.workflows.noRuns",
                )}
              </p>
            }
          >
            {(run) => (
              <>
                <div class="flex flex-wrap items-start justify-between gap-3">
                  <div aria-live="polite" role="status">
                    <h2 class="text-[14px] font-semibold">
                      {language.t("cowork.enterprise.workflows.reporting.selectedRun")}
                    </h2>
                    <p class="mt-2 text-[20px] font-semibold">
                      <span
                        classList={{
                          "text-v2-state-fg-danger": workflowRunStage(run()) === "failed",
                          "text-v2-state-fg-success": workflowRunStage(run()) === "ready",
                        }}
                      >
                        {language.t(`cowork.enterprise.workflows.reporting.stage.${workflowRunStage(run())}`)}
                      </span>
                    </p>
                    <p class="mt-2 break-all text-[11px] text-v2-text-text-muted">{run().id}</p>
                    <div class="mt-3 rounded-md border border-v2-border-weak bg-v2-surface-raised px-3 py-2 text-[12px]">
                      <p class="font-medium">{language.t("cowork.enterprise.workflows.reporting.outcome.systemResult")}</p>
                      <Show when={!independentReview()}>
                        <p class="mt-1 text-v2-text-text-muted">
                          {language.t("cowork.enterprise.workflows.reporting.outcome.validation", {
                            status: language.t(validationStatusKeys[run().validation_status]),
                          })}
                        </p>
                      </Show>
                      <p class="mt-1 text-v2-text-text-muted">
                        {language.t("cowork.enterprise.workflows.reporting.outcome.reportCount", {
                          count: run().artifacts.length,
                        })}
                      </p>
                    </div>
                    <Show when={!independentReview() && findingGroups().length}>
                      <section class="mt-3 rounded-md border border-v2-border-border-base px-3 py-2 text-[12px]">
                        <h3 class="font-medium">{language.t("cowork.enterprise.workflows.reporting.findings.title")}</h3>
                        <div class="mt-2 space-y-3">
                          <For each={findingGroups()}>
                            {(group) => (
                              <div>
                                <h4 class="font-medium">{language.t(findingKeys[group.classification])}</h4>
                                <ul class="mt-1 list-disc space-y-1 pl-4 text-v2-text-text-muted">
                                  <For each={group.findings}>{(finding) => <li>{finding.message}</li>}</For>
                                </ul>
                              </div>
                            )}
                          </For>
                        </div>
                      </section>
                    </Show>
                    <Show when={independentReview()}>
                      {(review) => (
                        <section class="mt-3 rounded-md border border-v2-border-border-base px-3 py-2 text-[12px]">
                          <h3 class="font-medium">{language.t("cowork.enterprise.workflows.reporting.results")}</h3>
                          <p class="mt-2 text-v2-text-text-muted">{review().summary}</p>
                          <Show when={review().verdict === "qualified"}>
                            <p class="mt-2 text-v2-state-fg-danger">
                              {language.t("cowork.enterprise.workflows.reporting.reviewWarning")}
                            </p>
                          </Show>
                          <Show when={review().findings.length}>
                            <ul class="mt-2 list-disc space-y-2 pl-4">
                              <For each={review().findings}>
                                {(finding) => <li><strong>{finding.location}:</strong> {finding.message} — {finding.suggested_fix}</li>}
                              </For>
                            </ul>
                          </Show>
                        </section>
                      )}
                    </Show>
                    <Show when={reviewHistory().length}>
                      <section class="mt-3 rounded-md border border-v2-border-border-base px-3 py-2 text-[12px]">
                        <h3 class="font-medium">{language.t("cowork.enterprise.workflows.reporting.results")}</h3>
                        <ol class="mt-2 space-y-3">
                          <For each={reviewHistory()}>
                            {(review) => <li>
                              <p class="font-medium">{review.index}. {language.t(review.verdict === "ready"
                                ? "cowork.enterprise.workflows.reporting.stage.ready"
                                : "cowork.enterprise.workflows.reporting.stage.needs_review")}</p>
                              <p class="mt-1 text-v2-text-text-muted">{review.summary}</p>
                              <ul class="mt-1 list-disc pl-4 text-v2-text-text-muted">
                                <For each={review.findings}>{(finding) => <li>{finding.location}: {finding.message}</li>}</For>
                              </ul>
                            </li>}
                          </For>
                        </ol>
                      </section>
                    </Show>
                    <Show when={extractionStatuses().length}>
                      <section class="mt-3 rounded-md border border-v2-border-border-base px-3 py-2 text-[12px]">
                        <h3 class="font-medium">{language.t("cowork.enterprise.workflows.reporting.extraction.title")}</h3>
                        <ul class="mt-2 space-y-1.5">
                          <For each={extractionStatuses()}>
                            {(file) => (
                              <li class="flex flex-wrap justify-between gap-x-4 gap-y-1">
                                <span class="min-w-0 break-all text-v2-text-text-muted">{file.name}</span>
                                <span class="font-medium">
                                  {language.t(file.outcome
                                    ? extractionStatusKeys[file.outcome.status]
                                    : "cowork.enterprise.workflows.reporting.extraction.unknown")}
                                </span>
                              </li>
                            )}
                          </For>
                        </ul>
                      </section>
                    </Show>
                  </div>
                  <div class="flex flex-wrap gap-2">
                    <button
                      type="button"
                      class={button}
                      disabled={
                        state.editing || state.starting || state.startPending || props.workflow.status !== "active"
                      }
                      onClick={() => void generate(run())}
                    >
                      {language.t(
                        readReportingConfig(run().input_snapshot.reporting) && configured()
                          ? "cowork.enterprise.workflows.reporting.rerun"
                          : "cowork.enterprise.workflows.reporting.configureNewRun",
                      )}
                    </button>
                    <Show
                      when={run().can_open_chat && run().session_id}
                      fallback={
                        <p class="max-w-xs text-[12px] text-v2-text-text-muted">
                          {language.t(
                            Object.hasOwn(chatReasonKeys, run().chat_unavailable_reason ?? "")
                              ? chatReasonKeys[run().chat_unavailable_reason as keyof typeof chatReasonKeys]
                              : !run().session_id
                                ? chatReasonKeys.missing_session
                                : "cowork.enterprise.workflows.reporting.chat.unavailable",
                          )}
                        </p>
                      }
                    >
                      <button
                        type="button"
                        class={button}
                        disabled={state.opening}
                        onClick={() => void openChat(run())}
                      >
                        {language.t("cowork.enterprise.workflows.openChat")}
                      </button>
                    </Show>
                    <button
                      type="button"
                      class={button}
                      disabled={state.refreshing}
                      onClick={() => void refreshSelected?.()}
                    >
                      {language.t("cowork.enterprise.workflows.reporting.refresh")}
                    </button>
                  </div>
                </div>
                <Show when={execution()}>
                  {(details) => (
                    <section class="mt-6 rounded-xl border border-v2-border-border-base bg-v2-background-bg-layer-01 p-5">
                      <h3 class="text-[14px] font-semibold">
                        {language.t("cowork.enterprise.workflows.reporting.execution.title")}
                      </h3>
                      <p class="mt-1 text-[12px] text-v2-text-text-muted">
                        {language.t("cowork.enterprise.workflows.reporting.execution.hint")}
                      </p>
                      <dl class="mt-4 grid gap-x-6 gap-y-4 text-[12px] sm:grid-cols-2 lg:grid-cols-3">
                        <div>
                          <dt class="text-v2-text-text-muted">
                            {language.t("cowork.enterprise.workflows.reporting.execution.template")}
                          </dt>
                          <dd class="mt-1 break-words font-medium">{details().templateID ?? "-"}</dd>
                        </div>
                        <div>
                          <dt class="text-v2-text-text-muted">
                            {language.t("cowork.enterprise.workflows.reporting.execution.version")}
                          </dt>
                          <dd class="mt-1 break-words font-medium">{details().templateVersion ?? "-"}</dd>
                        </div>
                        <div>
                          <dt class="text-v2-text-text-muted">
                            {language.t("cowork.enterprise.workflows.reporting.execution.reportType")}
                          </dt>
                          <dd class="mt-1 break-words font-medium">{details().reportType ?? "-"}</dd>
                        </div>
                        <div>
                          <dt class="text-v2-text-text-muted">
                            {language.t("cowork.enterprise.workflows.reporting.execution.jurisdiction")}
                          </dt>
                          <dd class="mt-1 break-words font-medium">
                            {details().jurisdiction === null
                              ? language.t("cowork.enterprise.workflows.reporting.execution.global")
                              : (details().jurisdiction ?? "-")}
                          </dd>
                        </div>
                        <div>
                          <dt class="text-v2-text-text-muted">
                            {language.t("cowork.enterprise.workflows.reporting.execution.runtimeAgent")}
                          </dt>
                          <dd class="mt-1 break-words font-medium">{details().runtimeAgent ?? "-"}</dd>
                        </div>
                        <div>
                          <dt class="text-v2-text-text-muted">
                            {language.t("cowork.enterprise.workflows.reporting.execution.catalogAgent")}
                          </dt>
                          <dd class="mt-1 break-words font-medium">{details().catalogRuntimeAgent ?? "-"}</dd>
                        </div>
                        <div class="sm:col-span-2 lg:col-span-3">
                          <dt class="text-v2-text-text-muted">
                            {language.t("cowork.enterprise.workflows.reporting.execution.parameters")}
                          </dt>
                          <dd class="mt-2">
                            <Show
                              when={details().parameters.length}
                              fallback={language.t("cowork.enterprise.workflows.reporting.execution.noParameters")}
                            >
                              <ul class="flex flex-wrap gap-2">
                                <For each={details().parameters}>
                                  {(parameter) => (
                                    <li class="rounded-md border border-v2-border-border-base bg-v2-background-bg-base px-2.5 py-1.5">
                                      <span class="text-v2-text-text-muted">{parameter.name}:</span>{" "}
                                      <span class="break-all font-medium">{parameter.value}</span>
                                    </li>
                                  )}
                                </For>
                              </ul>
                            </Show>
                          </dd>
                        </div>
                        <div class="sm:col-span-2 lg:col-span-3">
                          <dt class="text-v2-text-text-muted">
                            {language.t("cowork.enterprise.workflows.reporting.execution.requiredSkills")}
                          </dt>
                          <dd class="mt-2">
                            <Show
                              when={details().requiredSkills.length}
                              fallback={language.t("cowork.enterprise.workflows.reporting.execution.noSkills")}
                            >
                              <ul class="flex flex-wrap gap-2">
                                <For each={details().requiredSkills}>
                                  {(skill) => (
                                    <li class="rounded-full bg-v2-background-bg-base px-3 py-1.5 font-medium">
                                      {skill}
                                    </li>
                                  )}
                                </For>
                              </ul>
                            </Show>
                          </dd>
                        </div>
                      </dl>
                    </section>
                  )}
                </Show>
                <Show when={workflowRunIsActive(run()) || workflowRunStage(run()) === "ready"}>
                  <ol
                    class="mt-6 grid gap-3 rounded-xl bg-v2-background-bg-layer-01 p-5 sm:grid-cols-5"
                    aria-label={language.t("cowork.enterprise.workflows.reporting.progress")}
                  >
                    <For each={progressSteps}>
                      {(step, index) => (
                        <li
                          class="flex items-center gap-3 text-[12px] sm:flex-col sm:items-start"
                          aria-current={workflowRunStage(run()) === step ? "step" : undefined}
                        >
                          <span
                            class="grid size-8 shrink-0 place-items-center rounded-full border-2 border-v2-border-border-base"
                            classList={{
                              "border-v2-border-border-strong font-semibold":
                                progressSteps.indexOf(workflowRunStage(run()) as (typeof progressSteps)[number]) >=
                                index(),
                            }}
                          >
                            <Show
                              when={workflowRunStage(run()) === step && workflowRunIsActive(run())}
                              fallback={index() + 1}
                            >
                              <span class="size-4 animate-spin rounded-full border-2 border-v2-border-border-base border-t-v2-icon-icon-base" />
                            </Show>
                          </span>
                          <span>{language.t(`cowork.enterprise.workflows.reporting.stage.${step}`)}</span>
                        </li>
                      )}
                    </For>
                  </ol>
                </Show>
                <Show when={workflowRunStage(run()) === "ready" ||
                  (run().output_snapshot.report_contract_version === "draft.v1" && run().artifacts.length > 0) ||
                  independentReview()?.verdict === "qualified"}>
                  <button
                    type="button"
                    class="mt-4 rounded-lg bg-v2-background-bg-contrast px-5 py-3 text-[13px] font-semibold text-v2-text-text-contrast"
                    onClick={() => setQuery({ tab: "reports", run: run().id })}
                  >
                    {language.t("cowork.enterprise.workflows.reporting.viewReports")}
                  </button>
                </Show>
                <Show when={workflowRunStage(run()) === "waiting_for_input"}>
                  <p class="mt-4 text-[12px]">
                    {language.t(
                      run().can_open_chat
                        ? "cowork.enterprise.workflows.reporting.waitingOwner"
                        : "cowork.enterprise.workflows.reporting.waitingOther",
                    )}
                  </p>
                </Show>
                <Show when={workflowRunStage(run()) === "failed"}>
                  <p role="alert" class="mt-4 text-[12px] text-v2-state-fg-danger">
                    {language.t("cowork.enterprise.workflows.reporting.runFailed")}
                  </p>
                </Show>
                <Show when={run().metadata?.error_category || run().error_message}>
                  <p role="alert" class="mt-3 whitespace-pre-wrap break-words text-[12px] text-v2-state-fg-danger">
                    {language.t(
                      Object.hasOwn(errorCategoryKeys, String(run().metadata?.error_category))
                        ? errorCategoryKeys[String(run().metadata?.error_category)]
                        : "cowork.enterprise.workflows.reporting.runFailed",
                    )}
                  </p>
                </Show>
                <Show when={workflowRunStage(run()) === "needs_review"}>
                  <p class="mt-4 rounded-lg bg-v2-background-bg-layer-03 p-3 text-[12px]">
                    {language.t("cowork.enterprise.workflows.reporting.reviewWarning")}
                  </p>
                </Show>
              </>
            )}
          </Show>
        </main>
      </div>
      </Show>
    </div>
  )
}
